import { SxProps, Theme } from '@mui/material';
import { DESCRIPTION_GRAY } from '../../../styles/GlobalStyles';

export interface SharedCardOptions {
  direction?: 'row' | 'column';
  cursor?: 'default' | 'pointer';
}

export interface SharedActionLinkOptions {
  color?: string;
  gap?: string;
  padding?: string;
  hoverTextDecoration?: string;
  backgroundColor?: string;
  borderRadius?: string | number;
}

export const sharedCardStyles = {
  card: (theme: Theme, options: SharedCardOptions = {}) =>
    ({
      display: 'flex',
      width: '100%',
      flexDirection: options.direction || 'row',
      cursor: options.cursor || 'pointer',
      minHeight: '90px',
      transition: 'border-color 0.2s ease',
      border: '1px solid transparent',
      position: 'relative',
      '&:hover': {
        borderColor: theme.palette.primary.main,
      },
    }) as SxProps<Theme>,

  actionLink: (theme: Theme, options: SharedActionLinkOptions = {}) =>
    ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: options.gap,
      color: options.color || theme.palette.primary.main,
      fontSize: theme.typography.body2.fontSize,
      fontWeight: 400,
      textDecoration: 'none',
      cursor: 'pointer',
      opacity: 0,
      visibility: 'hidden',
      transition: 'opacity 0.2s ease, visibility 0.2s ease',
      backgroundColor: options.backgroundColor || theme.palette.background.paper,
      padding: options.padding || theme.spacing(0.25, 0.5),
      borderRadius: options.borderRadius ?? theme.shape.borderRadius,
      '&:hover': {
        textDecoration: options.hoverTextDecoration || 'underline',
      },
      '&:focus, &:focus-visible, &.Mui-focusVisible': {
        border: 'none !important',
        outline: 'none !important',
        boxShadow: 'none !important',
        textDecoration: 'underline',
      },
    }) as SxProps<Theme>,

  imageContainer: (theme: Theme) =>
    ({
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      alignSelf: 'flex-start',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(1.25),
      paddingRight: theme.spacing(1.25),
      flexShrink: 0,
      borderRadius: '3px',
      overflow: 'hidden',
    }) as SxProps<Theme>,

  image: () =>
    ({
      display: 'block',
      height: '100px',
      width: '100px',
      maxHeight: '100px',
      maxWidth: '100px',
      objectFit: 'cover',
      borderRadius: '3px',
      backgroundColor: '#F7F7F7',
      border: 'none',
    }) as SxProps<Theme>,

  contentContainer: (theme: Theme, padding: string) =>
    ({
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      flex: 1,
      padding,
      minWidth: 0,
    }) as SxProps<Theme>,

  title: (theme: Theme, interactive: boolean = false) =>
    ({
      fontSize: '18px',
      fontWeight: 400,
      color: theme.palette.primary.main,
      width: '100%',
      marginTop: '2px',
      marginBottom: theme.spacing(0.25),
      cursor: interactive ? 'pointer' : 'inherit',
      textDecoration: 'none',
      '&:hover': interactive
        ? {
            textDecoration: 'underline',
          }
        : undefined,
    }) as SxProps<Theme>,

  description: (theme: Theme, maxLines: number = 2) => {
    const lineHeightPx = maxLines > 2 ? 16 : 20;

    return ({
      fontSize: theme.typography.body2.fontSize,
      lineHeight: `${lineHeightPx}px`,
      maxHeight: `${maxLines * lineHeightPx}px`,
      fontStyle: 'italic',
      fontWeight: 400,
      color: DESCRIPTION_GRAY,
      width: '100%',
      display: '-webkit-box',
      WebkitLineClamp: maxLines,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      marginBottom: theme.spacing(0.25),
    }) as SxProps<Theme>;
  },
};

export default sharedCardStyles;
